import * as t from "io-ts";

import {
    LocalStorageBackend,
    Repository,
} from "@thelabnyc/thelabui/src/hooks/persistent-state";

import { Contribution as ContributionType, SuiteState } from "../engine/engine";
import { hub, skuCatalog } from "../engine/schema";

export const STORAGE_KEY = "sks-suite-builder:v1";

const DEFAULT_SUITE: SuiteState = { contributions: [] };

const Sku = t.keyof(skuCatalog);

const SpokeIdLookup = Object.fromEntries(
    hub.spokes.map((s) => [s.id, null]),
) as Record<(typeof hub.spokes)[number]["id"], null>;
const SpokeId = t.keyof(SpokeIdLookup);

const Contribution = t.exact(
    t.type({
        id: t.refinement(t.string, (s) => s.length <= 64, "BoundedId"),
        spoke: SpokeId,
        mainSku: Sku,
        accessorySkus: t.array(Sku),
        committedAt: t.number,
    }),
);

// Sanity ceilings against tampered storage; exceeded payloads truncate.
export const MAX_CONTRIBUTIONS = Object.keys(skuCatalog).length * 2;
export const MAX_ACCESSORIES_PER_CONTRIBUTION = 10;

/**
 * Decodes contributions individually and drops failures (unknown SKU
 * or spoke, malformed shape). Preferable to all-or-nothing, which
 * would wipe the whole suite when one SKU was retired between visits.
 */
const Contributions = new t.Type<
    ContributionType[],
    ContributionType[],
    unknown
>(
    "Contributions",
    (u): u is ContributionType[] =>
        Array.isArray(u) && u.every(Contribution.is),
    (input, ctx) => {
        if (!Array.isArray(input)) return t.failure(input, ctx);
        const kept: ContributionType[] = [];
        for (const item of input) {
            const decoded = Contribution.decode(item);
            if (decoded._tag !== "Right") continue;
            const c = decoded.right;
            kept.push({
                ...c,
                accessorySkus: c.accessorySkus.slice(
                    0,
                    MAX_ACCESSORIES_PER_CONTRIBUTION,
                ),
            });
            if (kept.length >= MAX_CONTRIBUTIONS) break;
        }
        return t.success(kept);
    },
    (a) => a,
);

export const SuiteStateCodec = t.exact(
    t.type({ contributions: Contributions }),
);

export const suiteRepo = new Repository({
    backend: new LocalStorageBackend({
        codec: SuiteStateCodec,
        defaultVal: DEFAULT_SUITE,
        storageKey: STORAGE_KEY,
    }),
    writeLockName: STORAGE_KEY,
    broadcastChannelName: STORAGE_KEY,
});

export { DEFAULT_SUITE };
